import { useEffect, useState } from "react";
import { Users, ArrowLeft, UserPlus, Inbox, Package, Send, MessageSquare, FileDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_LABELS = { password: "Email", google: "Google", apple: "Apple" };

const LIFECYCLE_META = {
  customer: { label: "Customer", color: "#B8A7E8" },
  interested: { label: "Interested", color: "#8FBFE8" },
  subscriber: { label: "Subscriber", color: "rgba(246,243,252,0.45)" },
};

const TIMELINE_META = {
  joined: { icon: UserPlus, color: "rgba(246,243,252,0.45)" },
  inquiry: { icon: Inbox, color: "#8FBFE8" },
  order: { icon: Package, color: "#E8CE8F" },
  email: { icon: Send, color: "#B8A7E8" },
  reply: { icon: MessageSquare, color: "#8FD8B8" },
};

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const Avatar = ({ user, size = "h-10 w-10", text = "text-sm" }) => (
  <span className={`flex ${size} shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#1c1e2d] font-display ${text} font-extrabold text-lilac`}>
    {user.picture ? (
      <img src={user.picture} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
    ) : (
      <img src="/brand-mark.png" alt="" className="h-3/5 w-3/5 rounded" />
    )}
  </span>
);

const UserProfile = ({ userId, onBack }) => {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/users/${userId}/profile`, { credentials: "include" });
        setProfile(res.ok ? await res.json() : { error: true });
      } catch {
        setProfile({ error: true });
      }
    })();
  }, [userId]);

  if (profile === null) return <p className="text-sm text-paper/45">Loading profile…</p>;
  if (profile.error) return <p className="text-sm text-paper/45">Couldn't load that profile.</p>;

  const { user, lifecycle, orders, inquiries, timeline } = profile;
  const lc = LIFECYCLE_META[lifecycle] || LIFECYCLE_META.subscriber;

  return (
    <div data-testid="user-profile">
      <button
        type="button"
        data-testid="user-profile-back"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-1.5 text-xs font-bold text-paper/55 transition-colors duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All users
      </button>

      <div className="mt-4 rounded-3xl border border-white/10 bg-card p-7">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar user={user} size="h-14 w-14" text="text-lg" />
          <div className="min-w-0">
            <p data-testid="profile-name" className="font-display text-xl font-extrabold text-paper">{user.name || "—"}</p>
            <p className="truncate text-sm text-paper/55">{user.email}</p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <span
              data-testid="profile-lifecycle"
              className="rounded-full px-3.5 py-1 text-xs font-bold uppercase tracking-[0.12em]"
              style={{ backgroundColor: `${lc.color}1f`, color: lc.color }}
            >
              {lc.label}
            </span>
            {user.role === "admin" && (
              <span className="rounded-full bg-lilac/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-lilac">Admin</span>
            )}
            {(user.providers || []).map((p) => (
              <span key={p} className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-paper/45">
                {PROVIDER_LABELS[p] || p}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-4 border-t border-white/[0.06] pt-4 text-xs text-paper/40">
          {user.created_at ? `Joined ${new Date(user.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}` : ""}
          {" · "}{orders.length} {orders.length === 1 ? "package" : "packages"}{" · "}{inquiries.length} {inquiries.length === 1 ? "inquiry" : "inquiries"}
        </p>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <div className="rounded-3xl border border-white/10 bg-card p-7">
            <h3 className="font-display text-lg font-extrabold text-paper">Packages</h3>
            {orders.length === 0 ? (
              <p data-testid="profile-no-packages" className="mt-3 text-sm text-paper/45">No packages yet.</p>
            ) : (
              <ul data-testid="profile-packages" className="mt-4 space-y-3">
                {orders.map((o) => (
                  <li key={o.id} data-testid={`profile-order-${o.id}`} className="rounded-xl bg-ink/50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-paper/85">{o.package_name}</p>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${
                        o.status === "active" ? "bg-lilac/15 text-lilac" : o.status === "complete" ? "bg-[#8FD8B8]/15 text-[#8FD8B8]" : "bg-[#E8CE8F]/15 text-[#E8CE8F]"
                      }`}>{o.status}</span>
                    </div>
                    <p className="mt-1.5 text-xs text-paper/45">
                      {o.delivered_count}/{o.deliverables.length} deliverables done
                      {o.files.length > 0 && ` · ${o.files.length} ${o.files.length === 1 ? "file" : "files"}`}
                    </p>
                    {o.files.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {o.files.map((f) => (
                          <a
                            key={f.id}
                            href={`${API}/api/files/${f.id}/download`}
                            data-testid={`profile-file-${f.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[10px] font-bold text-paper/55 transition-colors hover:border-lilac/40 hover:text-paper"
                          >
                            <FileDown className="h-3 w-3 text-lilac" aria-hidden="true" />
                            {f.filename}
                          </a>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-3xl border border-white/10 bg-card p-7">
            <h3 className="font-display text-lg font-extrabold text-paper">Inquiries</h3>
            {inquiries.length === 0 ? (
              <p data-testid="profile-no-inquiries" className="mt-3 text-sm text-paper/45">No inquiries from this person.</p>
            ) : (
              <ul data-testid="profile-inquiries" className="mt-4 space-y-3">
                {inquiries.map((inq) => (
                  <li key={inq.id} data-testid={`profile-inquiry-${inq.id}`} className="rounded-xl bg-ink/50 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-lilac/15 px-2.5 py-0.5 text-[10px] font-bold text-lilac">{inq.need}</span>
                      <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-paper/45">
                        {(inq.status || "new").replace("_", " ")}
                      </span>
                      <span className="ml-auto text-[10px] text-paper/35">{fmt(inq.created_at)}</span>
                    </div>
                    {inq.idea && <p className="mt-2 text-xs leading-relaxed text-paper/55">{inq.idea}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-card p-7">
          <h3 className="font-display text-lg font-extrabold text-paper">Everything, in order</h3>
          {timeline.length === 0 ? (
            <p className="mt-3 text-sm text-paper/45">No activity yet.</p>
          ) : (
            <ul data-testid="profile-timeline" className="mt-5 space-y-0">
              {timeline.map((e, i) => {
                const meta = TIMELINE_META[e.kind] || TIMELINE_META.joined;
                const Icon = meta.icon;
                return (
                  <li key={i} data-testid={`timeline-${e.kind}-${i}`} className="relative flex gap-3.5 pb-5">
                    {i < timeline.length - 1 && (
                      <span aria-hidden="true" className="absolute left-[13px] top-8 h-[calc(100%-24px)] w-px bg-white/[0.07]" />
                    )}
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: `${meta.color}1f`, color: meta.color }}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 pt-0.5">
                      <p className="text-sm font-bold text-paper/80">{e.title}</p>
                      {e.detail && <p className="mt-0.5 text-xs leading-relaxed text-paper/45">{e.detail}</p>}
                      <p className="mt-1 text-[10px] text-paper/30">{e.at ? fmt(e.at) : ""}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export function UsersPanel({ profileUserId, onOpenProfile, onCloseProfile }) {
  const [users, setUsers] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/users`, { credentials: "include" });
        setUsers(res.ok ? await res.json() : []);
      } catch {
        setUsers([]);
      }
    })();
  }, []);

  if (profileUserId) {
    return <UserProfile userId={profileUserId} onBack={onCloseProfile} />;
  }

  if (users === null) return <p className="text-sm text-paper/45">Loading…</p>;

  return (
    <div data-testid="admin-users-panel">
      {users.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-card p-12 text-center">
          <Users className="mx-auto h-9 w-9 text-paper/30" aria-hidden="true" />
          <p className="mt-4 text-sm text-paper/50">No registered users yet.</p>
        </div>
      ) : (
        <ul data-testid="admin-users-list" className="space-y-3">
          {users.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                data-testid={`admin-user-row-${u.id}`}
                onClick={() => onOpenProfile(u.id)}
                className="w-full rounded-2xl border border-white/[0.08] bg-card p-5 text-left transition-[border-color,background-color] duration-200 hover:border-lilac/30 hover:bg-white/[0.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3.5">
                    <Avatar user={u} />
                    <div>
                      <p className="text-sm font-bold text-paper">
                        {u.name || "—"}
                        {u.role === "admin" && (
                          <span data-testid={`user-role-${u.id}`} className="ml-2 rounded-full bg-lilac/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-lilac">
                            Admin
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-xs text-paper/50">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {(u.providers || []).map((p) => (
                      <span key={p} className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-paper/45">
                        {PROVIDER_LABELS[p] || p}
                      </span>
                    ))}
                    <span className="text-xs text-paper/35">
                      {u.created_at ? `Joined ${new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                    </span>
                  </div>
                </div>
                <div className="mt-3.5 flex flex-wrap items-center gap-2 border-t border-white/[0.06] pt-3.5">
                  {u.packages.length === 0 ? (
                    <span className="text-xs text-paper/35">No package</span>
                  ) : (
                    u.packages.map((pkg) => (
                      <span
                        key={`${u.id}-${pkg.package}`}
                        data-testid={`user-package-${u.id}-${pkg.package}`}
                        className={`rounded-full px-3 py-1 text-xs font-bold ${
                          pkg.status === "active"
                            ? "bg-lilac/15 text-lilac"
                            : pkg.status === "complete"
                              ? "bg-[#8FD8B8]/15 text-[#8FD8B8]"
                              : "bg-[#E8CE8F]/15 text-[#E8CE8F]"
                        }`}
                      >
                        {pkg.package_name} · {pkg.status}
                      </span>
                    ))
                  )}
                  <span className="ml-auto text-xs text-paper/40">
                    {u.inquiry_count} {u.inquiry_count === 1 ? "inquiry" : "inquiries"}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
